/**
 * Overview — the landing surface. A handful of KPI tiles plus the most recent
 * transactions, linking into the deeper explorer. Reuses the shared transaction
 * query so there's no bespoke data code.
 *
 * The KPI tiles are rendered with `ui-core` primitives (already in the eager
 * bundle) rather than `@authe/analytics-widgets`: that package pulls tremor +
 * recharts, and keeping it out of this eager landing page confines all charting
 * to the lazily-loaded `StatsPage` chunk — markedly smaller first paint.
 */
import * as React from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@authe/ui-core/api";
import { useSession } from "@authe/ui-core/session";
import { Card, CardContent, CardHeader, CardTitle, Skeleton, Button } from "@authe/ui-core";
import { Compass } from "lucide-react";
import {
  TransactionTable,
  useTransactionsQuery,
  txRowId,
  type TxRow,
} from "@authe/tx-table";
import { PageHeader } from "@/components/PageHeader";
import { OnchainActivityPanel } from "./onchain";
import { GettingStarted, useOnboardingProgress } from "./onboarding";
import { ROUTES } from "@/config";

interface StatMetric {
  title: string;
  value: number | string;
  delta?: number;
}

const compact = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});

function formatMetric(value: number | string): string {
  return typeof value === "number" ? compact.format(value) : value;
}

export function OverviewPage(): React.ReactElement {
  const navigate = useNavigate();
  const { session } = useSession();

  // Auth0-style onboarding: a fresh tenant lands on the "Getting started" guide
  // (progress inferred from real data); an established/dismissed tenant gets the
  // dashboard. `?guide=1` forces the guide either way. We hold off the decision
  // while the probes resolve so a set-up tenant never flashes the guide.
  const search = useSearch({ strict: false }) as { guide?: boolean };
  const onboarding = useOnboardingProgress();
  const showGuide =
    Boolean(search.guide) ||
    (!onboarding.dismissed && !onboarding.isLoading && !onboarding.allComplete);

  const stats = useQuery<{ metrics?: StatMetric[] }>((client, signal) =>
    client.get<{ metrics?: StatMetric[] }>("/stats/overview", { signal }),
  );
  const recent = useTransactionsQuery({ pageSize: 10 });

  const metrics = stats.data?.metrics ?? [];

  if (showGuide) {
    return <GettingStarted progress={onboarding} onDismiss={onboarding.dismiss} />;
  }

  return (
    <div>
      <PageHeader
        title={`Welcome${session?.user.name ? `, ${session.user.name}` : ""}`}
        description="A snapshot of cross-chain activity for your tenant."
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate({ to: ROUTES.gettingStarted })}
            >
              <Compass className="h-4 w-4" />
              Getting started
            </Button>
            <Button size="sm" onClick={() => navigate({ to: ROUTES.transactions })}>
              Explore transactions
            </Button>
          </div>
        }
      />

      {stats.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : (
        metrics.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {metrics.map((m) => (
              <Card key={m.title}>
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground">{m.title}</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
                    {formatMetric(m.value)}
                  </p>
                  {typeof m.delta === "number" && (
                    <p
                      className={
                        m.delta >= 0
                          ? "mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400"
                          : "mt-1 text-xs font-medium text-red-600 dark:text-red-400"
                      }
                    >
                      {m.delta >= 0 ? "+" : ""}
                      {m.delta}%
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      <OnchainActivityPanel />

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Recent transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <TransactionTable
            rows={recent.rows}
            isLoading={recent.isLoading}
            isFetching={recent.isFetching}
            error={recent.error}
            onRetry={recent.refetch}
            onRowClick={(row: TxRow) => {
              const id = txRowId(row);
              if (id) navigate({ to: "/transactions/$id", params: { id } });
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
}
